import _ from "lodash";
import React, { useState } from "react";

import { CarrierToInsurancePolicyNameMap } from "@/core/constants";
import { CATCH_ALL_INSURANCE_OPTIONS } from "@/core/constants/insurance";
import { createAcknowledgement } from "@/utils/acknowledgementUtils";
import { getCarrierMetadata } from "@/components/Common/InsuranceEligibility/carrierMetadata";
import { isUserPatient } from "@/utils/authToken";
import { transformDOBsToServerStrings } from "@/features/member-profile/(medical)/components/InsuranceInfo";

import { InsuranceAcknowledgementPopupHOC } from "../InsuranceAcknowledgementPopupHOC";
import { MemberProfileInsurance } from "../../MemberProfileInsurance";

const POLICY_HOLDER_OPTIONS = [
  { label: "Self", value: "Self" },
  { label: "Spouse", value: "Spouse" },
  { label: "Child", value: "Child" },
  { label: "Other", value: "Other" },
];

export function ProviderMemberProfileInsuranceHOC({
  addAcknowledgement,
  className,
  completedTitle,
  editTitle,
  emptyDetails,
  emptyTitle,
  formikProps,
  insurance,
  insuranceCardImgBackSrc,
  insuranceCardImgBackStatus,
  insuranceCardImgFrontSrc,
  insuranceCardImgFrontStatus,
  insuranceCompaniesOptions,
  isAso,
  isEditing,
  onAddButtonClick,
  onBrowseButtonClicked,
  onCancelButtonClick,
  onEditButtonClick,
  onFileDelete,
  onInsuranceCardImgBackClick,
  onInsuranceCardImgFrontClick,
  onSubmitButtonClick,
  stateOfInsurancePlanOptions,
  ...props
}) {
  const { errors, handleBlur, handleChange, handleFocus, touched, values } = formikProps;
  const { groupIdTooltipContents, memberIdPlaceholder, memberIdTip, memberIdTooltipContents } =
    getCarrierMetadata(_.get(values, "carrier"));
  const [noInsurance, setNoInsurance] = useState(
    CATCH_ALL_INSURANCE_OPTIONS.includes(_.get(values, "carrier")),
  );
  const [showAcknowledgementPopup, setShowAcknowledgementPopup] = useState(false);

  const mapDropDownValue = (value) => ({
    label: value,
    value: value,
  });

  const mappedCompletedValues = {
    groupId: _.get(values, "group_id"),
    insuranceCardImgBackSrc: _.get(values, "card_back"),
    insuranceCardImgFrontSrc: _.get(values, "card_front"),
    insuranceCompany: _.get(values, "carrier"),
    memberId: _.get(values, "member_id"),
    policyHolder: _.get(values, "policy_relationship"),

    policyHolderDOB: _.get(values, "policy_dob"),
    policyHolderName: _.get(values, "policy_holder_name"),
    stateOfInsurance: _.get(values, "state"),
  };

  const mappedEditValues = {
    groupId: _.get(values, "group_id"),
    insuranceCardImgBackSrc: _.get(values, "card_back"),
    insuranceCardImgFrontSrc: _.get(values, "card_front"),
    insuranceCompany: mapDropDownValue(_.get(values, "carrier")),
    memberId: _.get(values, "member_id"),
    policyHolder: mapDropDownValue(_.get(values, "policy_relationship")),

    policyHolderDOB: _.get(values, "policy_dob"),
    policyHolderName: _.get(values, "policy_holder_name"),
    stateOfInsurance: mapDropDownValue(_.get(values, "state")),
  };

  const mappedNames = {
    groupId: "group_id",
    insuranceBack: "card_back",
    insuranceCompany: "carrier",
    insuranceFront: "card_front",
    memberId: "member_id",
    policyHolder: "policy_relationship",
    policyHolderDOB: "policy_dob",
    policyHolderName: "policy_holder_name",
    stateOfInsurance: "state",
  };

  const mappedTouched = {
    groupId: _.get(touched, "group_id") || _.get(touched, "groupId"),
    insuranceCompany: _.get(touched, "carrier") || _.get(touched, "insuranceCompany"),
    memberId: _.get(touched, "member_id") || _.get(touched, "memberId"),
    policyHolder: _.get(touched, "policy_relationship") || _.get(touched, "policyHolder"),
    policyHolderDOB: _.get(touched, "policy_dob") || _.get(touched, "policyHolderDOB"),
    policyHolderName: _.get(touched, "policy_holder_name") || _.get(touched, "policyHolderName"),
    stateOfInsurance: _.get(touched, "state") || _.get(touched, "stateOfInsurance"),
  };

  const mappedErrors = {
    groupId: _.get(errors, "group_id"),
    insuranceCompany: _.get(errors, "carrier"),
    memberId: _.get(errors, "member_id"),
    policyHolder: _.get(errors, "policy_relationship"),
    policyHolderDOB: _.get(errors, "policy_dob"),
    policyHolderName: _.get(errors, "policy_holder_name"),
    stateOfInsurance: _.get(errors, "state"),
  };

  const mappedChangeHandler = (e) => {
    let name, value;

    if (!_.get(e, "target")) {
      name = "policyHolderDOB";
      value = e.value;
    } else {
      name = e.target.name;
      value = e.target.value;
    }
    const mappedName = mappedNames[name];
    let mappedValue = value;

    switch (name) {
      case "insuranceCompany":
        if (CATCH_ALL_INSURANCE_OPTIONS.includes(value)) {
          resetInsurance();
          setNoInsurance(true);
        } else {
          setNoInsurance(false);
        }
        break;
    }

    handleChange({ ...e, target: { ...e.target, name: mappedName, value: mappedValue } });
  };

  const resetInsurance = () => {
    for (const name in mappedNames) {
      if (name === "insuranceCompany") {
        continue;
      }
      formikProps.setFieldValue(mappedNames[name], "");
    }
  };

  const isInsuranceEmpty = (insurance) => {
    //No Value
    if (!insurance) {
      return true;
    }
    //Empty Object
    if (_.isEmpty(insurance)) {
      return true;
    }

    if (isMemberIdEmpty(insurance) && isCarrierEmpty(insurance)) {
      return true;
    }

    //Object with null or empty strings but "None" in string of image fields
    return false;
  };

  const isMemberIdEmpty = (insurance) => {
    //Object with all null values
    if (!_.get(insurance, "member_id")) {
      return true;
    }
    //Object with all empty string values
    if (_.get(insurance, "member_id") === "") {
      return true;
    }
    //Object with all a string value of None (Legacy)
    if (_.get(insurance, "member_id") === "None") {
      return true;
    }
  };

  const isCarrierEmpty = (insurance) => {
    //Object with all null values
    if (!_.get(insurance, "carrier")) {
      return true;
    }
    //Object with all empty string values
    if (_.get(insurance, "carrier") === "") {
      return true;
    }
    //Object with all a string value of None (Legacy)
    if (_.get(insurance, "carrier") === "None") {
      return true;
    }
  };

  const sanitizeFileNames = (fileName) => {
    if (fileName && fileName.includes("backend/insurance_cards/")) {
      return fileName.replace("backend/insurance_cards/", "");
    }

    return fileName;
  };

  const getFiles = () => {
    const files = [];
    let frontImg = mappedCompletedValues.insuranceCardImgFrontSrc;
    let backImg = mappedCompletedValues.insuranceCardImgBackSrc;

    if (frontImg && frontImg.includes("None")) {
      frontImg = null;
    }

    if (backImg && backImg.includes("None")) {
      backImg = null;
    }

    if (frontImg) {
      files.push(sanitizeFileNames(frontImg));
    }

    if (backImg) {
      files.push(sanitizeFileNames(backImg));
    }

    return files;
  };

  if (mappedCompletedValues.policyHolder) {
    mappedCompletedValues.policyHolder =
      mappedCompletedValues.policyHolder[0].toUpperCase() +
      mappedCompletedValues.policyHolder.slice(1);
  }

  const submitAndCloseAcknowledgement = async () => {
    const policyName = CarrierToInsurancePolicyNameMap[mappedCompletedValues.insuranceCompany];
    const acknowledgement = await createAcknowledgement({ consentResponse: true });

    setShowAcknowledgementPopup(false);
    addAcknowledgement(policyName, acknowledgement);
    onSubmitButtonClick();
  };

  const submitOrOpenAcknowledgement = () => {
    if (noInsurance && !showAcknowledgementPopup && isUserPatient()) {
      setShowAcknowledgementPopup(true);
    } else if (noInsurance && !isUserPatient()) {
      const policyName = CarrierToInsurancePolicyNameMap[mappedCompletedValues.insuranceCompany];

      addAcknowledgement(policyName, null);

      return onSubmitButtonClick();
    } else {
      return onSubmitButtonClick();
    }
  };

  return (
    <>
      <MemberProfileInsurance
        className={className}
        completedTitle={completedTitle}
        editTitle={editTitle}
        emptyDetails={emptyDetails}
        emptyTitle={emptyTitle}
        errors={mappedErrors}
        files={getFiles()}
        groupId={mappedCompletedValues.groupId}
        groupIdTooltipContents={groupIdTooltipContents}
        handleBlur={handleBlur}
        handleChange={mappedChangeHandler}
        handleFocus={handleFocus}
        insuranceCardImgBackSrc={insuranceCardImgBackSrc}
        insuranceCardImgBackStatus={insuranceCardImgBackStatus}
        insuranceCardImgFrontSrc={insuranceCardImgFrontSrc}
        insuranceCardImgFrontStatus={insuranceCardImgFrontStatus}
        insuranceCompaniesOptions={insuranceCompaniesOptions}
        insuranceCompany={mappedCompletedValues.insuranceCompany}
        isAso={isAso}
        isEditing={isEditing}
        isEmpty={isInsuranceEmpty(insurance)}
        memberId={mappedCompletedValues.memberId}
        memberIdPlaceholder={memberIdPlaceholder}
        memberIdTip={memberIdTip}
        memberIdTooltipContents={memberIdTooltipContents}
        noInsurance={noInsurance}
        onAddButtonClick={onAddButtonClick}
        onBrowseButtonClicked={onBrowseButtonClicked}
        onCancelButtonClick={onCancelButtonClick}
        onEditButtonClick={onEditButtonClick}
        onFileDelete={onFileDelete}
        onInsuranceCardImgBackClick={onInsuranceCardImgBackClick}
        onInsuranceCardImgFrontClick={onInsuranceCardImgFrontClick}
        onSubmitButtonClick={submitOrOpenAcknowledgement}
        policyHolder={mappedCompletedValues.policyHolder}
        policyHolderDOB={transformDOBsToServerStrings(mappedCompletedValues.policyHolderDOB)}
        policyHolderName={mappedCompletedValues.policyHolderName}
        policyHolderOptions={POLICY_HOLDER_OPTIONS}
        stateOfInsurancePlan={mappedCompletedValues.stateOfInsurance}
        stateOfInsurancePlanOptions={stateOfInsurancePlanOptions}
        touched={mappedTouched}
        values={mappedEditValues}
        {...props}
      />

      {showAcknowledgementPopup && (
        <InsuranceAcknowledgementPopupHOC
          acknowledgementType={mappedCompletedValues.insuranceCompany}
          disabled={!showAcknowledgementPopup}
          gaEventPrefix="member-settings"
          primaryOnClick={submitAndCloseAcknowledgement}
          secondaryOnClick={() => setShowAcknowledgementPopup(false)}
        />
      )}
    </>
  );
}
